import { useEffect } from "react";
import {
  IntegrationSetupPayload,
  LinkingFlowPageEnum,
  NextPagePayloadInfo,
} from "../../../config/types";
import { setLinkingFlowContext } from "../../stepTracking";
import { capturePageview } from "../../telemetry";

interface Params {
  isLoading: boolean;
  currentPage: LinkingFlowPageEnum;
  currentLinkPayload: NextPagePayloadInfo;
}

/**
 * `auth_option` only rides along on the connector-setup payload. Narrowing on the
 * key keeps the check honest if `NextPagePayloadInfo` ever grows its own field of
 * that name with a different shape, which a cast would hide.
 */
const authTypeFromPayload = (payload: NextPagePayloadInfo): string | undefined => {
  if (!payload || !("auth_option" in payload)) {
    return undefined;
  }
  return (payload as NextPagePayloadInfo & Pick<IntegrationSetupPayload, "auth_option">).auth_option
    ?.type;
};

/**
 * Publishes the step the user is on, so every event the telemetry plugin sends
 * can be attributed to a point in the flow and a funnel can be built per
 * connector.
 *
 * The customer organization is deliberately not read from the payload here. It
 * arrives once per session from the link token instead, so it is set before the
 * first step rather than on whichever step happens to carry it.
 */
const useStepTelemetry = ({ isLoading, currentPage, currentLinkPayload }: Params): void => {
  const connectorName = currentLinkPayload?.connector?.name;
  const authType = authTypeFromPayload(currentLinkPayload);
  const linkWasSuccessful = currentLinkPayload?.link_was_successful;

  useEffect(() => {
    // `isLoading` is the spinner that covers an in-flight request, not a step,
    // so it is skipped. Every LinkingFlowPageEnum value is published, including
    // the ones PageSwitcher renders through its default LoadingScreen
    // (INITIAL_STEP, LINKING_LOADING and friends): those are backend-driven
    // states a flow can stall in, and a funnel that hides them cannot show a
    // link attempt that never came back.
    if (isLoading || !currentPage) {
      return;
    }

    const stepChanged = setLinkingFlowContext({
      step: currentPage,
      connector: connectorName,
      authType,
    });

    // One pageview per transition. A payload update that leaves the step alone
    // still refreshes the dimensions above without re-emitting the step.
    if (stepChanged) {
      capturePageview({ link_was_successful: linkWasSuccessful });
    }
  }, [
    isLoading,
    currentPage,
    connectorName,
    authType,
    linkWasSuccessful,
  ]);
};

export default useStepTelemetry;
